import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { getUsername } from '../auth';
import ResetPasswordModal from '../components/ResetPasswordModal';
import UserModal from '../components/UserModal';
import { UserDto } from '../types';

const roleLabels: Record<string, string> = {
    ROLE_ADMIN: 'Администратор', ADMIN: 'Администратор',
    ROLE_CLINIC_HEAD: 'Главный врач', CLINIC_HEAD: 'Главный врач',
    ROLE_DOCTOR: 'Врач', DOCTOR: 'Врач',
};

const Users: React.FC = () => {
    const currentUsername = getUsername()?.toLowerCase();
    const [users, setUsers] = useState<UserDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [selected, setSelected] = useState<UserDto | null>(null);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get<UserDto[]>('/users');
            setUsers(response.data ?? []);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Не удалось загрузить пользователей');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { loadUsers(); }, [loadUsers]);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return users
            .filter((u) => !u.role?.includes('ADMIN'))
            .filter((u) => u.username?.toLowerCase() !== currentUsername)
            .filter((u) => !needle || [u.username, u.email, u.firstname, u.lastname, u.phone, u.iin]
                .some((value) => value?.toLowerCase().includes(needle)));
    }, [currentUsername, query, users]);

    const edit = async (user: UserDto) => {
        try {
            const response = await api.get<UserDto>(`/users/${user.id}`);
            setSelected(response.data);
            setModalOpen(true);
        } catch { setSelected(user); setModalOpen(true); }
    };

    const remove = async (user: UserDto) => {
        if (!window.confirm(`Удалить пользователя ${user.username}? Это действие нельзя отменить.`)) return;
        try { await api.delete(`/users/${user.id}`); await loadUsers(); }
        catch (e: any) { setError(e.response?.data?.message || 'Не удалось удалить пользователя'); }
    };

    return (
        <Container className="mt-4">
            <div className="page-heading">
                <div><h2><i className="bi bi-people text-primary me-2" />Пользователи</h2><div className="muted-small">Учётные записи, роли и доступ к системе</div></div>
                <Button onClick={() => { setSelected(null); setModalOpen(true); }}><i className="bi bi-person-plus me-2" />Добавить</Button>
            </div>
            {error && <Alert variant="danger">{error}</Alert>}
            <Card className="shadow-sm mb-3"><Card.Body>
                <InputGroup><InputGroup.Text><i className="bi bi-search" /></InputGroup.Text><Form.Control placeholder="Поиск по имени, логину, email, телефону или ИИН" value={query} onChange={(e) => setQuery(e.target.value)} /></InputGroup>
            </Card.Body></Card>
            <Card className="shadow-sm"><Card.Body className="p-0">
                {loading ? <div className="text-center py-5"><Spinner /></div> : <Table hover responsive className="mb-0 align-middle">
                    <thead className="table-light"><tr><th>Пользователь</th><th>Контакты</th><th>Роль</th><th>Организация</th><th>Регистрация</th><th /></tr></thead>
                    <tbody>{filtered.map((u) => <tr key={u.id}>
                        <td>
                            <div className="fw-semibold">{[u.lastname, u.firstname].filter(Boolean).join(' ') || u.username}</div>
                            <code>{u.username}</code>
                            {u.needPasswordChange && <Badge bg="warning" text="dark" className="ms-2">временный пароль</Badge>}
                        </td>
                        <td><div>{u.email}</div><small className="text-muted">{u.phone || '—'}</small></td>
                        <td><Badge bg={u.role.includes('ADMIN') ? 'primary' : u.role.includes('HEAD') ? 'info' : 'secondary'}>{roleLabels[u.role] || u.role}</Badge></td>
                        <td>{u.organizations?.map((o) => o.name).join(', ') || (u.organizationId ? `#${u.organizationId}` : '—')}</td>
                        <td>{u.registrationDate || '—'}</td>
                        <td className="text-end text-nowrap">
                            <Button size="sm" variant="outline-primary" className="me-2" onClick={() => edit(u)} title="Редактировать"><i className="bi bi-pencil" /></Button>
                            <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => { setSelected(u); setPasswordModalOpen(true); }} title="Сбросить пароль"><i className="bi bi-shield-lock" /></Button>
                            <Button size="sm" variant="outline-danger" onClick={() => remove(u)} title="Удалить"><i className="bi bi-trash" /></Button>
                        </td>
                    </tr>)}{filtered.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">Пользователи не найдены</td></tr>}</tbody>
                </Table>}
            </Card.Body></Card>
            <UserModal show={modalOpen} user={selected} onClose={() => setModalOpen(false)} onSaved={async () => { setModalOpen(false); await loadUsers(); }} />
            <ResetPasswordModal show={passwordModalOpen} user={selected} onClose={() => setPasswordModalOpen(false)} onSaved={async () => { setPasswordModalOpen(false); await loadUsers(); }} />
        </Container>
    );
};

export default Users;
